import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Table,
  Tooltip,
} from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { ShopStore } from 'stores';
import styled from 'styled-components';

const DarkHeaderModal = styled(Modal)`
  .ant-modal-content {
    padding: 0px;
    .ant-modal-close {
      .ant-modal-close-x {
        color: #ffffff;
      }
    }

    .ant-modal-header {
      background-color: #05103a;
      border-bottom: 0;
      padding: 20px 24px;
      .ant-modal-title {
        color: #ffffff;
      }
    }

    .ant-modal-body {
      padding: 20px 24px;
    }
  }

  .footer-row {
    margin-top: 21px;
  }

  .helper-text {
    padding: 8px;
    display: flex;
    align-items: center;
    min-height: 45px;

    p {
      font-size: 0.8rem;
      margin: 0;
      padding: 0;
    }
  }

  .save-btn {
    margin-left: 8px;
  }
`;

const AssetForm = styled(Row)`
  padding: 0;

  .ant-col-12 {
    .ant-form-item {
      margin: 0;
    }
  }
`;

const assetTypesOptions = [
  {
    value: 'vehicle',
    label: 'Vehicle',
  },
  {
    value: 'trailer',
    label: 'Trailer',
  },
  {
    value: 'other',
    label: 'Other',
  },
];

const AddAssetForm = ({ onSubmit, initialData, currentShopName, hideVin }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        ...initialData,
        fleet: initialData.fleet || currentShopName,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ fleet: currentShopName });
    }
  }, [initialData, form, currentShopName]);

  const onClickSave = async () => {
    try {
      const values = await form.validateFields();
      onSubmit({
        ...values,
        id: initialData?.id || null,
      });
      form.resetFields();
      form.setFieldsValue({ fleet: currentShopName });
    } catch {
      notification.error({
        message: 'Error',
        description: 'Please fill in all required fields',
      });
    }
  };

  return (
    <Form form={form} layout="vertical">
      <AssetForm gutter={[16, 16]}>
        <Col span={24}>
          <Form.Item
            name="assetType"
            label="Asset Type"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select
              placeholder="Select a type"
              style={{ width: '100%' }}
              allowClear
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
              options={assetTypesOptions}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="unitId"
            label="Unit ID"
            rules={[{ required: true, message: 'Please input the unit ID' }]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="fleet"
            label="Fleet"
            rules={[{ required: true, message: 'Please input asset fleet' }]}
          >
            <Input disabled />
          </Form.Item>
        </Col>
        {!hideVin && (
          <Col span={12}>
            <Form.Item name="vin" label="VIN/Serial No">
              <Input />
            </Form.Item>
          </Col>
        )}
        <Col span={hideVin ? 24 : 12}>
          <Form.Item
            name="year"
            label="Year"
            rules={[
              {
                pattern: /^\d{4}$/,
                message: 'The year must be a 4-digit number',
              },
            ]}
          >
            <Input maxLength={4} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="make" label="Make">
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="model" label="Model">
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="mileage"
            label="Mileage"
            rules={[
              {
                pattern: /^\d+$/,
                message: 'Mileage must be a number',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="engineHours"
            label="Engine Hours"
            rules={[
              {
                pattern: /^\d+$/,
                message: 'Engine Hours must be a number',
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="location" label="Location">
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="mainDriver" label="Main Driver">
            <Input />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Button
            type="default"
            style={{ float: 'right' }}
            onClick={onClickSave}
          >
            Save Unit
          </Button>
        </Col>
      </AssetForm>
    </Form>
  );
};

AddAssetForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    assetType: PropTypes.string,
    unitId: PropTypes.string,
    fleet: PropTypes.string,
    vin: PropTypes.string,
    year: PropTypes.string,
    make: PropTypes.string,
    model: PropTypes.string,
    mileage: PropTypes.string,
    engineHours: PropTypes.string,
    location: PropTypes.string,
    mainDriver: PropTypes.string,
  }),
  currentShopName: PropTypes.string,
  hideVin: PropTypes.bool,
};

const AssetsToAddTable = ({ assets, onDelete, onEdit }) => {
  const columns = [
    {
      title: 'Unit ID',
      dataIndex: 'unitId',
      key: 'unitId',
    },
    {
      title: 'VIN/Serial No',
      dataIndex: 'vin',
      key: 'vin',
      render: (text) =>
        text ? text : <EllipsisOutlined style={{ fontSize: '1rem' }} />,
    },
    {
      title: 'Fleet',
      dataIndex: 'fleet',
      key: 'fleet',
    },
    {
      title: 'Other',
      dataIndex: 'other',
      key: 'other',
      align: 'center',
      render: (_, row) => {
        // if there is no data, show the ellipsis icon
        const hasOtherData =
          row.assetType ||
          row.year ||
          row.make ||
          row.model ||
          row.mileage ||
          row.engineHours ||
          row.location ||
          row.mainDriver;

        const others = [
          { label: 'Asset Type', value: row.assetType },
          { label: 'Year', value: row.year },
          { label: 'Make', value: row.make },
          { label: 'Model', value: row.model },
          { label: 'Mileage', value: row.mileage },
          { label: 'Engine Hours', value: row.engineHours },
          { label: 'Location', value: row.location },
          { label: 'Main Driver', value: row.mainDriver },
        ];

        const onlyValidFields = others.filter((otherField) => otherField.value);

        const jsx = onlyValidFields.map((otherField, index) => (
          <Row key={index}>
            <Col span={24}>
              {otherField.label}: {otherField.value}
            </Col>
          </Row>
        ));

        return hasOtherData ? (
          <Tooltip placement="bottom" title={jsx}>
            <Button type="link" style={{ padding: 0 }}>
              More Details
            </Button>
          </Tooltip>
        ) : (
          <EllipsisOutlined style={{ fontSize: '1rem' }} />
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      align: 'right',
      render: (_, row) => (
        <>
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => onEdit(row)}
          >
            <EditOutlined />
          </Button>
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => onDelete(row.id)}
          >
            <DeleteOutlined />
          </Button>
        </>
      ),
    },
  ];

  return (
    <Table
      dataSource={assets}
      columns={columns}
      rowKey="id"
      scroll={{ x: 'max-content' }}
    />
  );
};

AssetsToAddTable.propTypes = {
  assets: PropTypes.array.isRequired,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
};

function AddAssetModal({ visible, onOk, onCancel, type, hideVin }) {
  const [assetsToAdd, setAssetsToAdd] = useState([]);
  const [initialData, setInitialData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Reset assetsToAdd every time the modal becomes visible
  useEffect(() => {
    setIsModalOpen(visible);
    if (visible) {
      setAssetsToAdd([]);
      setInitialData({});
    }
  }, [visible]);

  const handleCancel = () => {
    setIsModalOpen(false);
    onCancel();
  };

  const handleOk = (assets) => {
    setIsModalOpen(false);
    onOk(assets);
  };

  const onAddAssetToList = (values) => {
    setAssetsToAdd([
      ...assetsToAdd,
      {
        ...values,
        id: new Date().getTime(),
      },
    ]);
  };

  const deleteAsset = (id) => {
    setAssetsToAdd(assetsToAdd.filter((asset) => asset.id !== id));
  };

  const editAsset = (assetData) => {
    setInitialData(assetData);
  };

  return (
    <DarkHeaderModal
      open={isModalOpen}
      onCancel={handleCancel}
      title="Add Asset Without Telematics"
      footer={null}
      destroyOnClose={true}
      forceRender
    >
      <AddAssetForm
        initialData={initialData}
        hideVin={hideVin}
        onSubmit={({ id, ...values }) => {
          if (id) {
            // if the id is present, it means we are editing
            const updatedAssets = assetsToAdd.map((asset) =>
              asset.id === id ? { ...asset, ...values } : asset
            );
            setAssetsToAdd(updatedAssets);
            setInitialData({});
            return;
          }
          onAddAssetToList(values);
        }}
        currentShopName={ShopStore.currentShop?.name}
      />
      <AssetsToAddTable
        assets={assetsToAdd}
        onDelete={deleteAsset}
        onEdit={editAsset}
      />
      <Row className="footer-row" gutter={[16, 16]}>
        <Col className="helper-text" span={16}>
          <p>Click "Save" when finished adding units to your account</p>
        </Col>
        <Col span={8} style={{ display: 'flex' }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            className="save-btn"
            type="primary"
            onClick={() => handleOk(assetsToAdd)}
          >
            Save
          </Button>
        </Col>
      </Row>
    </DarkHeaderModal>
  );
}

AddAssetModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onOk: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  type: PropTypes.string,
  hideVin: PropTypes.bool,
};

AddAssetModal.defaultProps = {
  hideVin: false,
};

export default AddAssetModal;
